import { MoreVert } from '@mui/icons-material'
import { Alert, Box, CircularProgress, Divider, Menu, MenuItem, Paper, Stack, Tooltip, Typography } from '@mui/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import CustomTypography from '@/components/dataDisplay/CustomTypography'
import CustomButton from '@/components/inputs/CustomButton'
import CustomIconButton from '@/components/inputs/CustomIconButton'
import PageHeader from '@/components/layouts/PageHeader'
import { useAlertContext } from '@/contexts/AlertContext'
import { PrequalificationRouteInformation } from '@/features/prequalification/constants'
import DeleteResourceSetDialog from '@/features/prequalification/localControl/components/DeleteResourceSetDialog'
import MarketAccordion from '@/features/prequalification/localControl/components/MarketAccordion'
import PqPhaseChip from '@/features/prequalification/localControl/components/PqPhaseChip'
import RenameResourceSetDialog from '@/features/prequalification/localControl/components/RenameResourceSetDialog'
import ResourceSetResourcesSummary from '@/features/prequalification/localControl/components/ResourceSetResourcesSummary'
import type { ResourceSetTestActions } from '@/features/prequalification/localControl/components/resourceSetTestActions'
import {
  marketKey,
  resourceKey,
  testKey,
} from '@/features/prequalification/localControl/components/resourceSetTestActions'
import { pqClient } from '@/features/prequalification/localControl/endpoints/pqClient'
import { useCancelLocalPqRunMutation } from '@/features/prequalification/localControl/hooks/useCancelLocalPqRunMutation'
import { useClearLocalPqQueueMutation } from '@/features/prequalification/localControl/hooks/useClearLocalPqQueueMutation'
import { useLocalPqMarketsQuery } from '@/features/prequalification/localControl/hooks/useLocalPqMarketsQuery'
import { useLocalPqResourceSetQuery } from '@/features/prequalification/localControl/hooks/useLocalPqResourceSetQuery'
import {
  useInvalidateLocalPqResourceSetRunsQuery,
  useLocalPqResourceSetRunsQuery,
} from '@/features/prequalification/localControl/hooks/useLocalPqResourceSetRunsQuery'
import { useRunResourceSetMarketMutation } from '@/features/prequalification/localControl/hooks/useRunResourceSetMarketMutation'
import { useRunResourceSetTestMutation } from '@/features/prequalification/localControl/hooks/useRunResourceSetTestMutation'
import { useStartLocalPqRunMutation } from '@/features/prequalification/localControl/hooks/useStartLocalPqRunMutation'
import type { LocalPqRun, PqMarketCatalogueEntry, PqTestName } from '@/features/prequalification/localControl/types'
import {
  currentRunsForResources,
  latestRunsPerResourceTest,
  marketStatuses,
  resourceSetPhase,
} from '@/features/prequalification/localControl/utils/aggregation'
import {
  asCombinedCsvConflict,
  describeCsvDownloadFailure,
  isNoCsvDataError,
} from '@/features/prequalification/localControl/utils/csvErrors'
import { triggerCsvDownload } from '@/features/prequalification/localControl/utils/download'
import { testsOfMarket } from '@/features/prequalification/localControl/utils/marketCatalogue'
import { isTerminalPhase } from '@/features/prequalification/types'
import { errorHandler } from '@/utils/errorHandler'

const DATE_FORMAT = 'dd LLL yyyy, HH:mm'

function SummaryItem({ label, children }: Readonly<{ label: string; children: React.ReactNode }>) {
  return (
    <Stack sx={{ gap: 0.5 }}>
      <Typography color="text.secondary" variant="caption">
        {label}
      </Typography>
      {typeof children === 'string' ? <Typography variant="body1">{children}</Typography> : children}
    </Stack>
  )
}

function PrequalificationResourceSetDetails() {
  const { t } = useTranslation()
  const { resourceSetId = '' } = useParams()
  const { pushAlert } = useAlertContext()

  const { resourceSet, isLoading: isResourceSetLoading } = useLocalPqResourceSetQuery(
    { resourceSetId },
    { enabled: !!resourceSetId },
  )
  const { runs, isLoading: areRunsLoading } = useLocalPqResourceSetRunsQuery(
    { resourceSetId },
    { enabled: !!resourceSetId },
  )
  const { markets, isLoading: areMarketsLoading, refetch: refetchMarkets } = useLocalPqMarketsQuery()
  const invalidateRuns = useInvalidateLocalPqResourceSetRunsQuery()

  const { runResourceSetTest } = useRunResourceSetTestMutation()
  const { runResourceSetMarket } = useRunResourceSetMarketMutation()
  const { startRun } = useStartLocalPqRunMutation()
  const { cancelRun } = useCancelLocalPqRunMutation()
  const { clearQueue } = useClearLocalPqQueueMutation()

  // Every control awaiting a mutation, not just the most recent one: the actions are
  // independent and overlap freely, so a single slot would let a fast action's completion
  // re-enable the buttons of a slower one still in flight.
  const [pendingKeys, setPendingKeys] = useState<ReadonlySet<string>>(() => new Set())
  const [isRenaming, setIsRenaming] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)

  const resources = resourceSet?.resources ?? []
  // Scope runs to the set's current membership and collapse re-runs to the current run per
  // (resource, test) before any rollup. The backend keeps run history for removed resources
  // and accumulates a new run per "Queue again", so without this the header phase, the
  // active-run check and the stop/cancel targeting would count orphaned and stale runs that
  // have no row in the table. Until the resource set has loaded its membership is unknown, so
  // we only collapse re-runs. See `currentRunsForResources`.
  const allRuns = runs ?? []
  const resourceSetRuns = resourceSet
    ? currentRunsForResources(
        allRuns,
        resources.map((resource) => resource.resourceId),
      )
    : latestRunsPerResourceTest(allRuns)
  // Same derivation the list summary uses (`resourceSetPhase`), so the header and
  // the list row agree on a set's phase — NOT_STARTED until it has runs. Unknown until
  // the catalogue loads: without it every set would read as NOT_STARTED.
  const aggregate = markets ? resourceSetPhase(marketStatuses(resourceSetRuns, markets)) : null
  // A resource set can only be deleted once nothing is in flight; running tests must be
  // cancelled (or allowed to settle) first, so the action stays disabled until then.
  // While runs are still loading we treat the resource set as busy so delete can't slip
  // through before the run state is known.
  const hasActiveRuns = areRunsLoading || resourceSetRuns.some((run) => !isTerminalPhase(run.phase))

  function closeMenu() {
    setMenuAnchor(null)
  }

  function markPending(key: string) {
    setPendingKeys((keys) => new Set(keys).add(key))
  }

  function clearPending(key: string) {
    setPendingKeys((keys) => {
      const remaining = new Set(keys)
      remaining.delete(key)
      return remaining
    })
  }

  async function runAction(key: string, action: () => Promise<unknown>, successMessage: string, errorMessage: string) {
    markPending(key)
    try {
      await action()
      await invalidateRuns()
      pushAlert({ message: successMessage, severity: 'success' })
    } catch (err) {
      const error = errorHandler(err, errorMessage)
      pushAlert({ message: error.message, severity: 'error' })
    } finally {
      clearPending(key)
    }
  }

  function cancelRunsMatching(predicate: (run: LocalPqRun) => boolean): Promise<unknown> {
    const targets = resourceSetRuns.filter((run) => !isTerminalPhase(run.phase) && predicate(run))
    return Promise.all(targets.map((run) => cancelRun(run.id)))
  }

  const actions: ResourceSetTestActions = {
    pendingKeys,
    onRunMarket: (market: PqMarketCatalogueEntry, startAt) =>
      void runAction(
        marketKey(market.market),
        () => runResourceSetMarket({ resourceSetId, market: market.market, startAt }),
        t('prequalifications.resource_set.run_success'),
        t('prequalifications.resource_set.run_error'),
      ),
    onStopMarket: (market: PqMarketCatalogueEntry) =>
      void runAction(
        marketKey(market.market),
        () => {
          const testNames = new Set(testsOfMarket(market))
          return cancelRunsMatching((run) => testNames.has(run.testName))
        },
        t('prequalifications.resource_set.stop_success'),
        t('prequalifications.resource_set.stop_error'),
      ),
    onRunTest: (testName: PqTestName, startAt) =>
      void runAction(
        testKey(testName),
        () => runResourceSetTest({ resourceSetId, testName, startAt }),
        t('prequalifications.resource_set.run_success'),
        t('prequalifications.resource_set.run_error'),
      ),
    onStopTest: (testName: PqTestName) =>
      void runAction(
        testKey(testName),
        () => cancelRunsMatching((run) => run.testName === testName),
        t('prequalifications.resource_set.stop_success'),
        t('prequalifications.resource_set.stop_error'),
      ),
    onRunResource: (resourceId, testName, startAt) =>
      void runAction(
        resourceKey(resourceId, testName),
        () => startRun({ resourceSetId, resourceId, testName, startAt }),
        t('prequalifications.resource_set.runs.run_again_success'),
        t('prequalifications.resource_set.runs.run_again_error'),
      ),
    onStopResource: (runId) =>
      void runAction(
        runId,
        () => cancelRun(runId),
        t('prequalifications.resource_set.runs.stop_success'),
        t('prequalifications.resource_set.runs.stop_error'),
      ),
    onClearResource: (resourceId) =>
      void runAction(
        `clear:${resourceId}`,
        () => clearQueue(resourceId),
        t('prequalifications.resource_set.runs.clear_queue_success'),
        t('prequalifications.resource_set.runs.clear_queue_error'),
      ),
    onDownloadTest: (testName: PqTestName) => void handleDownloadTestCsv(testName),
    onDownloadResource: (runId) => void handleDownloadRunCsv(runId),
  }

  async function handleDownloadRunCsv(runId: string) {
    markPending(runId)
    try {
      const download = await pqClient.downloadRunCsv(runId)
      triggerCsvDownload(download, `local-pq-run-${runId}.csv`)
    } catch (err) {
      pushAlert({ message: describeRunCsvError(err, runId), severity: 'error' })
    } finally {
      clearPending(runId)
    }
  }

  /**
   * A bodiless 404 here means the run has no blob yet, which is not a fault: it is
   * scheduled, still streaming its first chunk, or finished without producing
   * anything. Which of those it is comes from the run's own phase — already polled
   * and on screen — so the two cases get their own sentence rather than one stretched
   * over both. A 404 that carries a body is something else and keeps its message.
   */
  function describeRunCsvError(err: unknown, runId: string): string {
    if (!isNoCsvDataError(err)) {
      return describeCsvDownloadFailure(err, t('prequalifications.resource_set.runs.download_error'))
    }
    const run = resourceSetRuns.find((candidate) => candidate.id === runId)
    if (run && !isTerminalPhase(run.phase)) {
      return t('prequalifications.resource_set.runs.download_no_data_yet')
    }
    return t('prequalifications.resource_set.runs.download_no_data')
  }

  async function handleDownloadTestCsv(testName: PqTestName) {
    const key = testKey(testName)
    markPending(key)
    try {
      const download = await pqClient.downloadTestCsv(resourceSetId, testName)
      triggerCsvDownload(download, `local-pq-test-${testName}-${resourceSetId}.csv`)
    } catch (err) {
      pushAlert({ message: describeTestCsvError(err), severity: 'error' })
    } finally {
      clearPending(key)
    }
  }

  /**
   * A 404 here is narrower than the per-run one: every resource's current run of the
   * test has finished and none produced data. `TestRow` will not let the download be
   * clicked while any run is still going, so this is the only reading left — but the
   * row's view of the runs is up to one poll old, so the backend still gets to
   * disagree. It answers a 409 then, naming the resources by id. It does the same when
   * some resources' data is incomplete or the runs disagree on columns, after a message
   * that says so. The set's membership is already loaded here, so show them by name.
   *
   * That message is shown whole rather than trimmed like an unrecognised body: the
   * backend writes it for this toast, and its actionable part comes last.
   */
  function describeTestCsvError(err: unknown): string {
    if (isNoCsvDataError(err)) return t('prequalifications.resource_set.test.download_no_data')
    const conflict = asCombinedCsvConflict(err)
    const genericMessage = t('prequalifications.resource_set.test.download_error')
    if (!conflict) return describeCsvDownloadFailure(err, genericMessage)
    const names = conflict.resourceIds
      .map((id) => resources.find((resource) => resource.resourceId === id)?.name ?? id)
      .join(', ')
    if (conflict.name === 'RUNS_IN_PROGRESS') {
      return t('prequalifications.resource_set.test.download_in_progress', { resources: names })
    }
    const affected = t('prequalifications.resource_set.test.download_affected_resources', { resources: names })
    return `${conflict.message || genericMessage}\n${affected}`
  }

  function renderMarkets() {
    if (isResourceSetLoading || areRunsLoading || areMarketsLoading) {
      return (
        <Stack sx={{ alignItems: 'center', py: 6 }}>
          <CircularProgress />
        </Stack>
      )
    }
    if (!markets) {
      return (
        <Alert
          action={
            <CustomButton color="inherit" size="small" onClick={() => void refetchMarkets()}>
              {t('prequalifications.resource_set.markets.retry')}
            </CustomButton>
          }
          severity="error"
        >
          {t('prequalifications.resource_set.markets.load_error')}
        </Alert>
      )
    }
    return (
      <Box>
        {markets.map((market) => (
          <MarketAccordion
            key={market.market}
            actions={actions}
            market={market}
            resources={resources}
            runs={resourceSetRuns}
          />
        ))}
      </Box>
    )
  }

  return (
    <>
      <PageHeader
        hiddenTitle
        breadcrumbItems={[
          {
            text: t('component.page_header.prequalifications'),
            to: PrequalificationRouteInformation.INDEX.navigationPath(),
          },
          {
            text: t('prequalifications.scope.local_control'),
            to: PrequalificationRouteInformation.INDEX.navigationPath(),
          },
        ]}
        pageTag="prequalifications-resource-set"
        pageTitle={resourceSet?.name ?? t('prequalifications.resource_set.title')}
      />

      <Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: 0.5, maxWidth: '100%' }}>
        <CustomTypography noWrap sx={{ minWidth: 0 }} variant="h1">
          {resourceSet?.name ?? t('prequalifications.resource_set.title')}
        </CustomTypography>

        <CustomIconButton
          Icon={MoreVert}
          aria-label={t('prequalifications.resource_set.actions.menu')}
          disabled={!resourceSet}
          size="small"
          sx={{ flexShrink: 0 }}
          tooltipProps={{ title: t('prequalifications.resource_set.actions.menu') }}
          onClick={(event) => setMenuAnchor(event.currentTarget)}
        />

        <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={closeMenu}>
          <MenuItem
            onClick={() => {
              setIsRenaming(true)
              closeMenu()
            }}
          >
            {t('prequalifications.resource_set.rename.action')}
          </MenuItem>

          <Tooltip title={hasActiveRuns ? t('prequalifications.resource_set.delete.blocked_active_runs') : ''}>
            <span>
              <MenuItem
                disabled={hasActiveRuns}
                sx={{ color: 'error.main' }}
                onClick={() => {
                  setIsDeleting(true)
                  closeMenu()
                }}
              >
                {t('prequalifications.resource_set.delete.action')}
              </MenuItem>
            </span>
          </Tooltip>
        </Menu>
      </Stack>

      {resourceSet && isRenaming && (
        <RenameResourceSetDialog
          currentName={resourceSet.name}
          resourceSetId={resourceSet.id}
          onClose={() => setIsRenaming(false)}
        />
      )}

      {resourceSet && isDeleting && (
        <DeleteResourceSetDialog
          resourceSetId={resourceSet.id}
          resourceSetName={resourceSet.name}
          onClose={() => setIsDeleting(false)}
        />
      )}

      <Paper sx={{ p: 3, mt: 3 }}>
        <Stack
          divider={<Divider flexItem orientation="vertical" />}
          sx={{ flexDirection: { xs: 'column', md: 'row' }, gap: 3, flexWrap: 'wrap' }}
        >
          <SummaryItem label={t('prequalifications.resource_set.summary.status')}>
            {aggregate ? <PqPhaseChip phase={aggregate} size="small" /> : '-'}
          </SummaryItem>
          <SummaryItem label={t('prequalifications.resource_set.summary.created')}>
            {resourceSet?.requestedAt.toFormat(DATE_FORMAT) ?? '-'}
          </SummaryItem>
          <SummaryItem label={t('prequalifications.resource_set.summary.resources', { count: resources.length })}>
            <ResourceSetResourcesSummary resourceSetId={resourceSetId} resources={resources} />
          </SummaryItem>
        </Stack>
      </Paper>

      <Stack sx={{ mt: 3, gap: 2 }}>
        <Typography variant="h6">{t('prequalifications.resource_set.markets.title')}</Typography>

        {renderMarkets()}
      </Stack>
    </>
  )
}

export default PrequalificationResourceSetDetails
